import { Field } from "@components/common/Field";
import { Place } from "@gen/place/v1/place_pb";
import {
  BookingType,
  DAP,
  TauxPec,
  Transportation,
  TrainCabinClass,
  PlaneCabinClass,
} from "@gen/dap/v1/dap_pb";
import { formatCoordinates } from "@utils/coordinates";

// a field of a DAP to display on the detail view
export const fields: (
  t: (s: string) => string,
  caisseOptions?: { label: string; value: number }[],
) => Field<DAP, keyof DAP>[] = (t, caisseOptions = []) => [
  {
    key: "status",
    display: (value) => {
      switch (value) {
        case 1:
          return <span className="text-secondary">{t("ok")}</span>;
        case 2:
          return <span className="text-error-strong">{t("error")}</span>;
        default:
          return t("N/A");
      }
    },
  },
  {
    key: "number",
    type: "text",
    sortable: true,
    filterable: true,
  },
  {
    key: "lastName",
    type: "text",
    editable: true,
    sortable: true,
    filterable: true,
    creation: { rules: { required: t("form.fieldRequired") } },
  },
  {
    key: "firstName",
    type: "text",
    editable: true,
    sortable: true,
    filterable: true,
    creation: { rules: { required: t("form.fieldRequired") } },
  },
  {
    key: "caisseId",
    type: "select",
    search: true,
    sortable: true,
    filterable: true,
    options: caisseOptions,
  },
  {
    key: "type",
    display: (v) => {
      switch (v) {
        case BookingType.ONLINE:
          return t("online").toUpperCase();
        case BookingType.OFFLINE:
          return t("offline").toUpperCase();
        default:
          return t("N/A");
      }
    },
    options: [
      {
        label: t("online"),
        value: BookingType.ONLINE,
      },
      {
        label: t("offline"),
        value: BookingType.OFFLINE,
      },
    ],
  },
  {
    key: "tauxPec",
    type: "select",
    editable: true,
    condition: (dap) => dap.isPecModifiable,
    span: 2,
    options: [
      {
        label: "55%",
        value: TauxPec.TAUX_PEC_55,
      },
      {
        label: "100%",
        value: TauxPec.TAUX_PEC_100,
      },
    ],
    filterable: true,
  },
  {
    key: "origin",
    type: "place",
    editable: true,
    display: (value) => formatPlace(value as Place),
    filterable: true,
  },
  {
    key: "destination",
    type: "place",
    editable: true,
    display: (value) => formatPlace(value as Place),
    filterable: true,
  },
  {
    key: "transportations",
    type: "select",
    multi: true,
    editable: true,
    options: [
      {
        label: t("train"),
        value: Transportation.TRAIN,
      },
      {
        label: t("plane"),
        value: Transportation.PLANE,
      },
      {
        label: t("boat"),
        value: Transportation.BOAT,
      },
    ],
    filterable: true,
  },
  {
    //TODO: we should fix this typo
    key: "companions",
    label: t("admin.dap.companions"),
    type: "number",
    min: 0,
    editable: true,
    creation: { rules: { required: t("form.fieldRequired") } },
  },
  {
    key: "journeysBooked",
    type: "number",
    min: 0,
    editable: true,
    creation: {
      rules: {
        validate: (value, values) =>
          !values.maxJourneys ||
          Number(value) <= Number(values.maxJourneys) ||
          t("admin.dap.journeysBooked.max"),
        min: {
          value: 0,
          message: t("admin.dap.journeysBooked.min"),
        },
        required: t("admin.dap.journeysBooked.required"),
      },
    },
  },
  {
    key: "maxJourneys",
    type: "number",
    min: 1,
    editable: true,
    creation: { rules: { required: t("form.fieldRequired") } },
  },
  {
    key: "trainCabinClass",
    type: "select",
    editable: true,
    display: (value) => {
      switch (value) {
        case TrainCabinClass.PREMIER:
          return t("class.first");
        case TrainCabinClass.SECOND:
          return t("class.second");
        default:
          return t("N/A");
      }
    },
    options: [
      {
        label: t("class.first"),
        value: TrainCabinClass.PREMIER,
      },
      {
        label: t("class.second"),
        value: TrainCabinClass.SECOND,
      },
    ],
  },
  {
    key: "planeCabinClass",
    type: "select",
    editable: true,
    options: [
      {
        label: t("class.business"),
        value: PlaneCabinClass.BUSINESS,
      },
      {
        label: t("class.economypremium"),
        value: PlaneCabinClass.ECONOMY_PREMIUM,
      },
      {
        label: t("class.economy"),
        value: PlaneCabinClass.ECONOMY,
      },
    ],
  },
  {
    key: "comment",
    type: "text",
    editable: true,
    span: 2,
  },
];

function formatPlace(place: Place | undefined): string {
  if (!place) {
    return "";
  }
  if (place.name != "") {
    return place.name;
  }
  if (place.latitude != 0 && place.longitude != 0) {
    return formatCoordinates(place?.latitude, place?.longitude);
  }
  if (place.zipCode != "") {
    return place.zipCode;
  }
  return place.id;
}
